import { JSX, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { Field, FieldType } from "core/field/FieldService";

import { AddEvent, Event } from "utils/history/Event";
import { asTitle } from "utils/Utils";

import FormFieldRow from "core/field/form/FormFieldRow";
import NewFormFieldRow from "core/field/form/NewFormFieldRow";
import useListHeadInfo from "lib/list/useListHeadInfo";

const supportedTypes = [
	FieldType.BOOLEAN,
	FieldType.CALCULATION,
	FieldType.DATE,
	FieldType.DECIMAL,
	FieldType.GROUP,
	FieldType.INTEGER,
	FieldType.IMAGE,
	FieldType.SELECT,
	FieldType.MULTI_SELECT,
	FieldType.TEXT,
	FieldType.URL,
	FieldType.UPLOAD
];

export type FormFieldTableProps = {
	elements: Field[];
	editable: boolean;

	onChange: (event: Event) => void;
	onInsert: (event: Event) => void;
};

export default function FormFieldTable(
	props: Readonly<FormFieldTableProps>
): JSX.Element {
	const { t } = useTranslation();

	const listHeadInfo = useListHeadInfo();

	const handleSelectAll = useCallback(
		(_event: React.ChangeEvent<HTMLInputElement>, select: boolean) => {
			listHeadInfo.selectAll(select);
		},
		[listHeadInfo.selectAll]
	);

	const handleChange = useCallback(
		(index: number, event: Event): void => {
			event.applyContext("/" + index);
			props.onChange(event);
		},
		[props.onChange]
	);

	const handleInsert = useCallback(
		(field: Field): void => {
			const path = `/${props.elements.length}`;
			props.onInsert(new AddEvent(path, field));
		},
		[props.elements.length, props.onInsert]
	);

	function getRows(): JSX.Element[] {
		const elements: JSX.Element[] = [];
		for (const [index, element] of props.elements.entries()) {
			elements.push(
				<FormFieldRow
					key={element.uuid}
					index={index}
					field={element}
					onChange={event => handleChange(index, event)}
				/>
			);
		}

		return elements;
	}

	return (
		<Table>
			<TableHead>
				<TableRow>
					<TableCell
						id="select-header"
						align="center"
						padding="checkbox">
						<Checkbox
							id="select-all"
							disabled={!props.editable}
							checked={listHeadInfo.allSelected}
							indeterminate={listHeadInfo.someSelected}
							onChange={handleSelectAll}
						/>
					</TableCell>
					<TableCell id="number-header" sx={{ width: "6ch" }}>
						#
					</TableCell>
					<TableCell id="expand-header" sx={{ width: "6ch" }} />
					<TableCell id="type-header" sx={{ width: "20ch" }}>
						{asTitle(t("type"))}
					</TableCell>
					<TableCell id="name-header">{asTitle(t("name"))}</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{getRows()}
				{props.editable && (
					<NewFormFieldRow
						supportedTypes={supportedTypes}
						key={props.elements.length}
						onCreate={handleInsert}
					/>
				)}
			</TableBody>
		</Table>
	);
}
